import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Modal,
  Pagination,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Close, Create, Search } from "@mui/icons-material";

import {
  Container,
  Content,
  Form,
  HeaderModal,
  InputStyled,
  PaginationStyled,
  Title,
} from "./style";
import { Header } from "../Header";
import { regex } from "../../utils/regex";
import { Loading } from "../Loading";
import {
  apiAdmin,
  apiAutenticacao,
  apiOperation,
  headers,
} from "../../services/api";
import { Usuario } from "../../models/usuario";
import { IUser } from "../Login/ILogin";
import { getSessionUser } from "../../utils/users";
import useDebouncedState from "../../utils/useDebouncedState";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  bgcolor: "background.paper",
  border: "2px solid var(--border-hairline)",
  boxShadow: 24,
  p: 4,
  borderRadius: "10px",
};

export interface EmpresaUnidade {
  empresaUnidadeId: string;
  empresaId: string;
  descricao: string;
  usuarioInclusao: string;
  dataInclusao: Date;
  dataEdicao: Date;
  usuarioEdicao: string;
}

const user = {
  id: null,
  nome: "",
  email: "",
  perfil: "",
  unidade: "",
  cargos: "",
  ativo: true,
};

let usuarioLogado: IUser | null = null;

export const Users = () => {
  const [form, setForm] = useState({ ...user });
  const [validation, setValidation] = useState({ ...user });
  const [showLoading, setShowLoading] = useState(true);
  const {
    value: searchTerm,
    setValue: setSearchTerm,
    debouncedValue: debouncedSearchTerm,
  } = useDebouncedState("", 1000);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("");
  let navigate = useNavigate();
  const [historyList, setUserList] = useState<Usuario[]>([]);
  const [unitList, setUnitList] = useState<EmpresaUnidade[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [myRoles, setMyRoles] = useState("");

  const onPaginationChange = (_: unknown, value: number) => {
    setPage(value);
  };

  const handleClose = () => {
    setForm({ ...user });
    setValidation({ ...user });
    setOpen(false);
  };

  const getUnitList = async (email: string) => {
    const config = {
      headers: headers(),
    };
    const unidades = await apiOperation.get<EmpresaUnidade[]>(
      "empresaunidade/" + (email ?? ""),
      config
    );

    setUnitList(unidades.data);
  };

  const getUsers = useCallback(
    async (busca: string | null = null, pageNumber: number = page) => {
      try {
        setShowLoading(true);
        const config = {
          headers: headers(),
        };

        const parameters = `?pagina=${pageNumber}&registrosPorPagina=10${
          busca ? `&busca=${busca}` : ""
        }`;
        const response = await apiAutenticacao.get(
          "usuario/listar" + parameters,
          config
        );

        setUserList([...response.data.valor.dados]);
        setTotalPages(response.data.valor.quantidadePaginas || 10);
      } catch (error: any) {
        console.error("Erro ao buscar usuários:", error);
      } finally {
        setShowLoading(false);
      }
    },
    [page]
  );

  const getRoles = useCallback(async () => {
    const config = {
      headers: headers(),
    };
    const response = await apiAdmin.get("cargos", config);
    setRoles([...response.data]);
  }, []);

  // useEffect único para inicialização e busca de usuários
  useEffect(() => {
    const initialize = async () => {
      usuarioLogado = getSessionUser();

      if (usuarioLogado?.perfil !== "Admin") {
        navigate("/historico");
        return;
      }

      if (
        usuarioLogado?.cargosUsuario.includes("ACESSOTOTALCREDITON") ||
        usuarioLogado?.cargosUsuario.includes("ACESSOADMINEMPRESA")
      ) {
        await getRoles();
      }

      setMyRoles(usuarioLogado?.cargosUsuario || "");
    };

    initialize();
  }, [navigate, getRoles]);

  // useEffect para busca e paginação - reseta página quando busca muda
  useEffect(() => {
    if (debouncedSearchTerm) {
      setPage(1);
      getUsers(debouncedSearchTerm, 1);
    } else {
      getUsers(null, page);
    }
  }, [debouncedSearchTerm, page, getUsers]);

  const handleOpen = async (mode: string, user?: any) => {
    setShowLoading(true);
    setMode(mode);
    if (user)
      setForm({
        id: user.usuarioId,
        nome: user.nome,
        email: user.email,
        perfil: user.perfil,
        unidade: user.unidade,
        cargos: user.cargos || "",
        ativo: user.status,
      });

    await getUnitList(user?.email || "");
    setShowLoading(false);
    setOpen(true);
  };

  const handleChange = (event: any) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const formValidation = () => {
    let isValid = true;
    let nome = "";
    let email = "";
    let perfil = "";
    if (form.nome.length <= 0) {
      nome = "Nome do Usuário Obrigatório.";
      isValid = false;
    }
    if (form.email.length <= 0) {
      email = "E-mail Obrigatório.";
      isValid = false;
    }
    if (!regex.email.test(form.email.toLocaleLowerCase())) {
      email = "E-mail com formato inválido";
      isValid = false;
    }
    if (form.perfil.length === 0) {
      perfil = "Perfil de Usuário Obrigatório.";
      isValid = false;
    }

    isValid
      ? setValidation({ ...user })
      : setValidation({ ...user, nome, email, perfil });
    return isValid;
  };

  const onSave = () => {
    if (formValidation()) mode === "create" ? createUser() : editUser();
  };

  const createUser = async () => {
    setShowLoading(true);
    try {
      const config = {
        headers: headers(),
      };

      await apiAutenticacao.post(
        "usuario/inserir",
        {
          nome: form.nome,
          email: form.email,
          status: form.ativo,
          perfil: form.perfil,
          unidade: form.unidade,
          cargos: form.cargos,
        },
        config
      );

      handleClose();
      await getUsers(debouncedSearchTerm || null);
      setShowLoading(false);
    } catch (error: any) {
      setShowLoading(false);
    }
    return;
  };

  const editUser = async () => {
    setShowLoading(true);
    try {
      const config = {
        headers: headers(),
      };

      await apiAutenticacao.post(
        "usuario/atualizar",
        {
          nome: form.nome,
          email: form.email,
          status: form.ativo,
          unidade: form.unidade,
          cargos: form.cargos,
          perfil: form.perfil,
        },
        config
      );

      handleClose();
      await getUsers(debouncedSearchTerm || null);
      setShowLoading(false);
    } catch (error: any) {
      setShowLoading(false);
    }

    return;
  };

  return (
    <>
      <Header />
      <Container>
        <Content>
          <Title>Usuários</Title>
          <Button
            sx={{
              width: "10.4em",
              marginLeft: "auto",
            }}
            color="secondary"
            variant="contained"
            onClick={() => handleOpen("create")}
          >
            Novo Usuario
          </Button>
        </Content>
        <Card sx={{ minWidth: 275 }}>
          {showLoading ? (
            <Loading hidden={!showLoading} />
          ) : (
            <div>
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                <TextField
                  size="small"
                  placeholder="Buscar usuário..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  sx={{ minWidth: "300px" }}
                />
              </CardContent>

              <CardContent>
                <TableContainer>
                  <Table
                    sx={{ minWidth: 650 }}
                    size="small"
                    aria-label="a dense table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell align="left">Nome </TableCell>
                        <TableCell align="left">E-mail</TableCell>
                        <TableCell align="left">Perfil</TableCell>
                        {(myRoles.includes("ACESSOTOTALCREDITON") ||
                          myRoles.includes("ACESSOADMINEMPRESA")) && (
                          <>
                            <TableCell align="left">Unidade</TableCell>
                            <TableCell align="left">Cargo</TableCell>
                          </>
                        )}
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center"></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {historyList.map((item) => (
                        <TableRow key={item.usuarioId} hover>
                          <TableCell component="th" scope="row">
                            {item.nome}
                          </TableCell>
                          <TableCell align="left">{item.email}</TableCell>
                          <TableCell align="left">{item.perfil}</TableCell>
                          {(myRoles.includes("ACESSOTOTALCREDITON") ||
                            myRoles.includes("ACESSOADMINEMPRESA")) && (
                            <>
                              <TableCell align="left">{item.unidade}</TableCell>
                              <TableCell align="left">{item.cargos}</TableCell>
                            </>
                          )}
                          <TableCell align="center">
                            <Chip
                              color={item.status ? "primary" : `default`}
                              label={item.status ? "Ativo" : "Inativo"}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              color="primary"
                              aria-label="Visualizar"
                              onClick={() => handleOpen("edit", item)}
                            >
                              <Create titleAccess="Editar" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Pagination
                    sx={PaginationStyled}
                    count={totalPages}
                    page={page}
                    onChange={onPaginationChange}
                  />
                  <Modal
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                    aria-describedby="modal-modal-description"
                    sx={{ minWidth: "60%" }}
                    style={{ minWidth: "60%" }}
                  >
                    <Box sx={style}>
                      <HeaderModal>
                        <Typography
                          id="modal-modal-title"
                          variant="h6"
                          component="h2"
                        >
                          Novo Usuário
                        </Typography>
                        <IconButton onClick={handleClose}>
                          <Close />
                        </IconButton>
                      </HeaderModal>
                      <Form autoComplete="false">
                        <TextField
                          id="nome"
                          label="Nome"
                          variant="outlined"
                          name="nome"
                          type={"text"}
                          style={InputStyled}
                          value={form.nome}
                          required={true}
                          onChange={(event: any) => handleChange(event)}
                          error={validation.nome ? true : false}
                          helperText={validation.nome}
                        />
                        <TextField
                          id="email"
                          label="E-mail"
                          variant="outlined"
                          name="email"
                          type={"text"}
                          required={true}
                          style={InputStyled}
                          value={form.email}
                          onChange={(event: any) => handleChange(event)}
                          error={validation.email ? true : false}
                          helperText={validation.email}
                        />
                        {mode === "create" && (
                          <TextField
                            id="perfil"
                            label="Perfil"
                            variant="outlined"
                            name="perfil"
                            style={InputStyled}
                            select
                            value={form.perfil}
                            required={true}
                            onChange={(event: any) => handleChange(event)}
                            error={validation.perfil ? true : false}
                            helperText={validation.perfil}
                          >
                            <MenuItem key={"operador"} value={"operador"}>
                              {"Operador"}
                            </MenuItem>
                            <MenuItem key={"Admin"} value={"Admin"}>
                              {"Administrador"}
                            </MenuItem>
                          </TextField>
                        )}

                        {(myRoles.includes("ACESSOTOTALCREDITON") ||
                          myRoles.includes("ACESSOADMINEMPRESA")) && (
                          <>
                            <Autocomplete
                              selectOnFocus
                              clearOnBlur
                              options={unitList}
                              getOptionLabel={(option) => option.descricao}
                              value={
                                unitList.find(
                                  (unit) => unit.descricao === form.unidade
                                ) || null
                              }
                              onChange={(
                                event,
                                value: EmpresaUnidade | null
                              ) => {
                                setForm({
                                  ...form,
                                  unidade: value?.descricao || "",
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  style={InputStyled}
                                  label="Unidade"
                                />
                              )}
                            />

                            <Autocomplete
                              selectOnFocus
                              clearOnBlur
                              options={roles}
                              value={form.cargos}
                              onChange={(event, value: string | null) => {
                                setForm({
                                  ...form,
                                  cargos: value || "",
                                });
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  style={InputStyled}
                                  label="Cargo"
                                />
                              )}
                            />
                          </>
                        )}

                        <FormControlLabel
                          control={
                            <Switch
                              checked={form.ativo}
                              onChange={() =>
                                setForm({ ...form, ativo: !form.ativo })
                              }
                              name="ativo"
                            />
                          }
                          label="Ativo"
                        />
                        <Divider sx={{ margin: "15px 0" }} />
                        <Button
                          sx={{
                            width: "10.4em",
                            marginLeft: "auto",
                          }}
                          color="secondary"
                          variant="contained"
                          onClick={() => onSave()}
                        >
                          Salvar
                        </Button>
                      </Form>
                    </Box>
                  </Modal>
                </TableContainer>
              </CardContent>
            </div>
          )}
        </Card>
        <Loading mode="blurry" hidden={!showLoading}></Loading>
      </Container>
    </>
  );
};
